import React, { useState } from 'react';
import { DollarSign, Users, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { AppView } from '../../types';

interface TaxTipSplitCalculatorProps {
  onNavigateToView: (view: AppView) => void;
  defaultTab?: 'vat' | 'tip' | 'split';
}

export const TaxTipSplitCalculator: React.FC<TaxTipSplitCalculatorProps> = ({ onNavigateToView, defaultTab = 'vat' }) => {
  const [subTab, setSubTab] = useState<'vat' | 'tip' | 'split'>(defaultTab);

  // VAT State
  const [vatAmount, setVatAmount] = useState<string>('499');
  const [vatRate, setVatRate] = useState<string>('20');
  const [vatMode, setVatMode] = useState<'add' | 'remove'>('add');

  // Tip & Split State
  const [billAmount, setBillAmount] = useState<string>('120');
  const [tipPct, setTipPct] = useState<string>('15');
  const [peopleCount, setPeopleCount] = useState<string>('4');
  const [splitMode, setSplitMode] = useState<'equal' | 'unequal'>('equal');

  // Calculations
  // VAT
  const amt = parseFloat(vatAmount) || 0;
  const rate = parseFloat(vatRate) || 0;
  let netAmt = 0;
  let taxAmt = 0;
  let grossAmt = 0;

  if (vatMode === 'add') {
    netAmt = amt;
    taxAmt = netAmt * (rate / 100);
    grossAmt = netAmt + taxAmt;
  } else {
    grossAmt = amt;
    netAmt = grossAmt / (1 + rate / 100);
    taxAmt = grossAmt - netAmt;
  }

  // Tip & Split
  const bill = parseFloat(billAmount) || 0;
  const tipP = parseFloat(tipPct) || 0;
  const people = parseInt(peopleCount) || 1;

  const tipAmount = bill * (tipP / 100);
  const totalWithTip = bill + tipAmount;
  const perPerson = people > 0 ? totalWithTip / people : totalWithTip;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                YamooPay Everyday Money Engine (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Calculateur de TVA, Pourboire & Partage d'Addition
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="flex border-b border-[#E2E8F0] gap-6 mb-8">
          {[
            { id: 'vat', label: 'Moteur TVA, TPS & Taxe de Vente' },
            { id: 'tip', label: 'Calculateur de Pourboire' },
            { id: 'split', label: 'Partage d\'Addition (Split Bill)' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id as any)}
              className={`pb-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                subTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* VAT / Tax Calculator */}
        {subTab === 'vat' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Calculateur d'Impôt Indirect (TVA / TPS)</h3>
              
              <div className="flex gap-2 mb-6">
                {[
                  { id: 'add', label: 'Ajouter la taxe (HT → TTC)' },
                  { id: 'remove', label: 'Retirer la taxe incluse (TTC → HT)' },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setVatMode(m.id as any)}
                    className={`py-2 px-4 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                      vatMode === m.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-[#F8FAFC] text-[#475569] border-[#CBD5E1] hover:bg-[#F1F5F9]'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">
                    {vatMode === 'add' ? 'Montant Net (HT) ($)' : 'Montant Brut (TTC) ($)'}
                  </label>
                  <input
                    type="number"
                    value={vatAmount}
                    onChange={(e) => setVatAmount(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Taux de Taxe (%)</label>
                  <input
                    type="number"
                    value={vatRate}
                    onChange={(e) => setVatRate(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#475569]">
                Règle officielle appliquée : extraction inverse rigoureuse pour les montants TTC inclus. Pas d'approximation linéaire.
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-2">Résumé Fiscal</div>
                <div className="space-y-3 mt-4">
                  <div>
                    <div className="text-xs text-amber-200">Montant Net (HT)</div>
                    <div className="text-2xl font-bold">${netAmt.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-amber-200">Montant Taxe ({rate}%)</div>
                    <div className="text-xl font-bold text-amber-300">${taxAmt.toFixed(2)}</div>
                  </div>
                  <div className="pt-3 border-t border-white/10">
                    <div className="text-xs text-amber-200">Montant Brut (TTC)</div>
                    <div className="text-3xl font-extrabold text-white">${grossAmt.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tip & Split Bill */}
        {(subTab === 'tip' || subTab === 'split') && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Calculateur de Pourboire & Division d'Addition</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Montant de la Facture ($)</label>
                  <input
                    type="number"
                    value={billAmount}
                    onChange={(e) => setBillAmount(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Pourcentage de Pourboire (%)</label>
                  <input
                    type="number"
                    value={tipPct}
                    onChange={(e) => setTipPct(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Nombre de Personnes</label>
                  <input
                    type="number"
                    value={peopleCount}
                    onChange={(e) => setPeopleCount(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="flex gap-2 mb-6">
                {[10, 15, 18, 20].map((p) => (
                  <button
                    key={p}
                    onClick={() => setTipPct(p.toString())}
                    className={`py-1.5 px-3 rounded-lg text-xs font-bold border transition-colors cursor-pointer ${
                      tipPct === p.toString()
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-[#F8FAFC] text-[#0F172A] border-[#CBD5E1] hover:bg-[#F1F5F9]'
                    }`}
                  >
                    {p} %
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-violet-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-violet-300 uppercase tracking-wider mb-2">Partage & Total</div>
                <div className="space-y-4 mt-2">
                  <div>
                    <div className="text-xs text-violet-200">Pourboire calculé</div>
                    <div className="text-xl font-bold">${tipAmount.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-violet-200">Facture Totale avec Pourboire</div>
                    <div className="text-2xl font-bold text-white">${totalWithTip.toFixed(2)}</div>
                  </div>
                  <div className="pt-4 border-t border-white/10">
                    <div className="text-xs text-violet-200">Montant par personne ({people} pers.)</div>
                    <div className="text-3xl font-extrabold text-violet-300">${perPerson.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
